import { TrackJS } from 'trackjs';
import { subscriptionRecovery } from './subscriptionRecovery';

const safeStringify = (value) => {
    try {
        return JSON.stringify(value);
    } catch {
        return '[unserializable]';
    }
};

const getMessage = (error) => {
    if (!error) return '';
    if (typeof error === 'string') return error;
    if (error.message) return error.message;
    if (!Array.isArray(error.errors)) return safeStringify(error);

    return error.errors
        .map((item) => item?.message)
        .filter(Boolean)
        .join(' | ');
};

const getHttpStatusReason = (message) => {
    if (message.includes('status code 400')) return 'http_400';
    if (message.includes('status code 401')) return 'http_401';
    if (message.includes('status code 403')) return 'http_403';
    if (message.includes('status code 404')) return 'http_404';
    if (message.includes('status code 500')) return 'http_500';
    if (message.includes('status code 502')) return 'http_502';
    if (message.includes('status code 503')) return 'http_503';
    if (message.includes('status code 504')) return 'http_504';

    return undefined;
};

export const getErrorReason = (error) => {
    const message = getMessage(error);
    const httpReason = getHttpStatusReason(message);

    if (httpReason) return httpReason;
    if (message.includes('ExpiredTokenException')) return 'expired_token';
    if (message.includes('UnauthorizedException')) return 'unauthorized';
    if (message.includes('not authorized')) return 'unauthorized';
    if (message.includes('Connection handshake error')) return 'handshake';
    if (message.includes('Connection closed')) return 'connection_closed';
    if (message.includes('MaxSubscriptionsReachedError')) return 'max_subscriptions';
    if (message.includes('Timeout disconnect')) return 'timeout_disconnect';
    if (message.includes('Access token is unavailable')) return 'access_token_unavailable';
    if (message.includes('Network error')) return 'network_error';

    return 'unknown';
};

const addMetadata = (metadata) => {
    Object.entries(metadata).forEach(([key, value]) => {
        if (value === undefined || value === '') return;

        const text = typeof value === 'string' ? value : safeStringify(value);
        TrackJS.addMetadata(`app.${key}`, text.slice(0, 500));
    });
};

const removeMetadata = (metadata) => {
    Object.keys(metadata).forEach((key) => {
        TrackJS.removeMetadata(`app.${key}`);
    });
};

const warnReporterFailure = (operation, error) => {
    if (import.meta.env.DEV) {
        console.warn(`TrackJS ${operation} failed`, error);
    }
};

export const reportHandledError = (eventName, error, metadata = {}) => {
    const reason = getErrorReason(error);
    const message = getMessage(error);
    const trackedError = new Error(`${eventName}: ${reason}`);
    const originalStack = error?.stack || error?.networkError?.stack;

    if (originalStack) {
        trackedError.stack = originalStack;
    }

    const trackJsMetadata = {
        eventName,
        reason,
        originalMessage: message,
        route: typeof window === 'undefined' ? undefined : window.location?.pathname,
        ...metadata,
    };

    // TrackJS metadata is session/page scoped and sent with each captured error.
    // Add it only around this explicit report, then remove it so later unrelated
    // errors do not inherit this cpId/operation context.
    // Docs:
    // - https://docs.trackjs.com/browser-agent/sdk-reference/agent-methods/#addmetadata
    // - https://docs.trackjs.com/browser-agent/sdk-reference/agent-methods/#removemetadata
    try {
        addMetadata(trackJsMetadata);
        TrackJS.track(trackedError);
    } catch (trackJsError) {
        warnReporterFailure('reporting', trackJsError);
    } finally {
        try {
            removeMetadata(trackJsMetadata);
        } catch (trackJsError) {
            warnReporterFailure('metadata cleanup', trackJsError);
        }
    }

    if (import.meta.env.DEV) {
        console.error(eventName, { reason, ...metadata }, error);
    }
};

export const reportSubscriptionError = ({ error, entityId, entityType, operation, source }) => {
    reportHandledError('subscription.failed', error, {
        entityId,
        entityType,
        operation,
        source,
    });

    // The coordinator ignores non-recoverable reasons;
    // recoverable errors start (or join) one debounced recovery cycle.
    subscriptionRecovery.notify(getErrorReason(error));
};

// TEMPORARY RECOVERY VERIFIER: Dev/Test only, for the post-deployment observation period.
// TODO: Remove these tracked outcome events after recovery is validated, before reducing
// subscription error noise. Completion is not a live-event acknowledgement.
/** @param {import('./subscriptionRecovery').RecoveryOutcome} result */
export const reportRecoveryOutcome = (result) => {
    const environment = import.meta.env.VITE_APP_STAGE;
    if (environment !== 'Dev' && environment !== 'Test') return;

    const eventName = `subscription.recovery.${result.outcome}`;
    reportHandledError(eventName, new Error(eventName), {
        environment,
        bundleUrl: import.meta.url,
        cycleId: result.cycleId,
        reason: result.reason,
        attempts: result.attempts,
        durationMs: result.durationMs,
        recoveryEvidence: result.outcome === 'completed'
            ? 'callback_resolved_rebuild_requested'
            : 'retries_exhausted',
    });
};
